import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/dateFormat";
import { useI18n } from "@/context/I18nContext";

export const PendingUsersPanel = ({ onChanged }) => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);

  const load = () => api.get("/municipalities/pending-users").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const act = async (u, action) => {
    try {
      await api.post(`/municipalities/pending-users/${u.id}/${action}`);
      toast.success(t(action === "approve" ? "user_approved_toast" : "user_rejected_toast"));
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (items.length === 0) return null;
  return (
    <Card className="border-primary/40" data-testid="pending-users-panel">
      <CardContent className="space-y-3 p-6">
        <div className="flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">{t("pending_users_title")} ({items.length})</h3>
        </div>
        <ul className="divide-y divide-border">
          {items.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`pending-user-${u.id}`}>
              <div className="min-w-0">
                <div className="font-medium">{u.name} <span className="text-xs text-muted-foreground">· {u.email}</span></div>
                <div className="text-sm text-muted-foreground">{u.address}</div>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline">{t(`status_${u.account_status}`)}</Badge>
                  {u.created_at && formatDateTime(u.created_at)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => act(u, "approve")} data-testid={`pending-user-approve-${u.id}`}>{t("approve")}</Button>
                <Button size="sm" variant="ghost" onClick={() => act(u, "reject")} data-testid={`pending-user-reject-${u.id}`}>{t("reject")}</Button>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
